import { type ChartType, getField, grandTotal, type ObjectDef, type ReportResult } from '@crm/shared';
import { type ReactNode, useState } from 'react';
import { formatMoney } from '../lib/format';
import { useSession } from '../lib/session';

/** Categorical hues in fixed order; a 9th category folds into "Other" instead of a new hue. */
const SERIES = ['var(--s1)', 'var(--s2)', 'var(--s3)', 'var(--s4)', 'var(--s5)', 'var(--s6)', 'var(--s7)', 'var(--s8)'];
const MAX_CATEGORIES = 8;

export interface Datum { label: string; value: number }

const compact = (n: number) => new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n);

/** Folds categories beyond the eighth into "Other", so colour always follows the category, never its rank. */
function foldOther(data: Datum[], max = MAX_CATEGORIES): Datum[] {
  if (data.length <= max) return data;
  const head = data.slice(0, max - 1);
  const rest = data.slice(max - 1).reduce((t, d) => t + d.value, 0);
  return [...head, { label: 'Other', value: rest }];
}

/** Horizontal bars: best for comparing categories with long names. One series, one hue. */
export function BarChart({ data, format }: { data: Datum[]; format: (n: number) => string }) {
  const rows = data.slice(0, 15);
  const max = Math.max(1, ...rows.map((d) => d.value));
  const rowH = 28;
  const labelW = 130;
  const w = 480;
  const plotW = w - labelW - 70;
  return (
    <svg className="chart" viewBox={`0 0 ${w} ${rows.length * rowH + 8}`} role="img" aria-label="Bar chart">
      {rows.map((d, i) => {
        const bw = Math.max(0, (d.value / max) * plotW);
        const y = i * rowH + 6;
        return (
          <g key={d.label + i}>
            <text x={labelW - 8} y={y + 12} textAnchor="end" className="ch-label">{d.label.length > 18 ? `${d.label.slice(0, 17)}…` : d.label}</text>
            {/* Hit target spans the whole row; the mark itself stays thin. */}
            <rect x={labelW} y={y - 4} width={plotW + 60} height={rowH - 2} fill="transparent"><title>{`${d.label}: ${format(d.value)}`}</title></rect>
            <rect className="ch-bar-h" x={labelW} y={y + 2} width={bw} height={14} rx={4} fill={SERIES[0]} pointerEvents="none" style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }} />
            <text x={labelW + bw + 6} y={y + 13} className="ch-value">{compact(d.value)}</text>
          </g>
        );
      })}
      <line x1={labelW} x2={labelW} y1={0} y2={rows.length * rowH + 4} className="ch-axis" />
    </svg>
  );
}

/** Vertical columns: good for ordered categories such as months or stages. */
export function ColumnChart({ data, format }: { data: Datum[]; format: (n: number) => string }) {
  const cols = data.slice(0, 24);
  const max = Math.max(1, ...cols.map((d) => d.value));
  const w = 480;
  const h = 220;
  const pad = { l: 44, b: 40, t: 10 };
  const plotH = h - pad.b - pad.t;
  const step = (w - pad.l - 8) / Math.max(1, cols.length);
  const bw = Math.min(28, step - 2);
  const ticks = [0, 0.5, 1].map((p) => p * max);
  return (
    <svg className="chart" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Column chart">
      {ticks.map((t) => {
        const y = pad.t + plotH - (t / max) * plotH;
        return (
          <g key={t}>
            <line x1={pad.l} x2={w - 4} y1={y} y2={y} className="ch-grid" />
            <text x={pad.l - 6} y={y + 4} textAnchor="end" className="ch-tick">{compact(t)}</text>
          </g>
        );
      })}
      {cols.map((d, i) => {
        const bh = (d.value / max) * plotH;
        const x = pad.l + i * step + (step - bw) / 2;
        return (
          <g key={d.label + i}>
            <rect x={pad.l + i * step} y={pad.t} width={step} height={plotH} fill="transparent"><title>{`${d.label}: ${format(d.value)}`}</title></rect>
            <rect className="ch-bar-v" x={x} y={pad.t + plotH - bh} width={bw} height={bh} rx={4} fill={SERIES[0]} pointerEvents="none" style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }} />
            {cols.length <= 12 && (
              <text x={x + bw / 2} y={h - pad.b + 14} textAnchor="middle" className="ch-tick">{d.label.length > 9 ? `${d.label.slice(0, 8)}…` : d.label}</text>
            )}
          </g>
        );
      })}
      <line x1={pad.l} x2={w - 4} y1={pad.t + plotH} y2={pad.t + plotH} className="ch-axis" />
    </svg>
  );
}

export interface Series { name: string; values: number[] }

/** A smooth line through the points (monotone in x, no overshoot between points). */
function smoothPath(pts: readonly (readonly [number, number])[]): string {
  if (!pts.length) return '';
  if (pts.length < 3) return pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x},${y}`).join(' ');
  let d = `M${pts[0]![0]},${pts[0]![1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[i]!;
    const [x1, y1] = pts[i + 1]!;
    const mx = (x1 - x0) / 2;
    d += ` C${x0 + mx},${y0} ${x1 - mx},${y1} ${x1},${y1}`;
  }
  return d;
}

/**
 * Lines over an ordered x axis. Interactive: summary chips that switch series on and off (each keeps its colour),
 * a crosshair with a floating tooltip on hover (or ←/→ when focused), point markers, a soft area under each line
 * and the latest value labelled at the end of each line.
 */
export function LineChart({ x, series, format, since = 0 }: { x: string[]; series: Series[]; format: (n: number) => string; since?: number }) {
  const [hover, setHover] = useState<number | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const all = series.slice(0, MAX_CATEGORIES).map((s, i) => ({ ...s, color: SERIES[i]!, id: `ln${i}` }));
  const shown = all.filter((s) => !hidden.has(s.name));
  const w = 900;
  const h = 300;
  const pad = { l: 56, r: shown.length && shown.length <= 5 ? 128 : 16, t: 18, b: 34 };
  const top = Math.max(1, ...shown.flatMap((s) => s.values));
  const max = niceMax(top);
  const plotW = w - pad.l - pad.r;
  const px = (i: number) => pad.l + (x.length <= 1 ? plotW / 2 : (i / (x.length - 1)) * plotW);
  const py = (v: number) => pad.t + (h - pad.t - pad.b) * (1 - v / max);
  if (!x.length) return <p className="empty-line">No data yet.</p>;
  const last = x.length - 1;
  const xTicks = x.length <= 6 ? x.map((_, i) => i) : [0, 1, 2, 3, 4, 5].map((k) => Math.round((k / 5) * last));
  // The change shown on each chip: from the point given (e.g. a week ago), else from the first one.
  const base = Math.max(0, Math.min(since, x.length - 1));
  const change = (s: Series, i: number) => (s.values[i] ?? 0) - (s.values[base] ?? 0);
  const toggle = (name: string) => setHidden((h0) => { const n = new Set(h0); if (n.has(name)) n.delete(name); else if (n.size < all.length - 1) n.add(name); return n; });
  const tipLeft = hover === null ? 0 : (px(hover) / w) * 100;
  const topIndex = shown.reduce((best, s, i) => ((s.values[last] ?? 0) > (shown[best]?.values[last] ?? 0) ? i : best), 0);
  // End labels sorted top to bottom, each at least 30 px below the previous one (two lines of text).
  const endLabels = [...shown].map((s) => ({ name: s.name, color: s.color, value: s.values[last] ?? 0, at: py(s.values[last] ?? 0) }))
    .sort((a, b) => a.at - b.at)
    .reduce<{ name: string; color: string; value: number; at: number; y: number }[]>((acc, l) => {
      const prev = acc[acc.length - 1];
      acc.push({ ...l, y: Math.max(l.at, prev ? prev.y + 30 : pad.t + 4) });
      return acc;
    }, []);
  // Labels pushed below the plot are lifted back up, keeping their spacing.
  const overflow = endLabels.length ? endLabels[endLabels.length - 1]!.y + 13 - (h - pad.b + 8) : 0;
  if (overflow > 0) endLabels.forEach((l) => { l.y -= overflow; });
  const sign = (n: number) => (n > 0 ? '+' : n < 0 ? '−' : '±');
  return (
    <figure className="chart-fig ch-interactive">
      <div className="ch-chips">
        {all.map((s) => {
          const off = hidden.has(s.name);
          const d = change(s, last);
          return (
            <button key={s.name} type="button" className={`ch-chip${off ? ' off' : ''}`} aria-pressed={!off} onClick={() => toggle(s.name)} title={off ? 'Show this line' : 'Hide this line'}>
              <span className="ch-chip-name"><i style={{ background: s.color }} />{s.name}</span>
              <b>{format(s.values[last] ?? 0)}</b>
              {x.length > 1 && <span className={`ch-delta ${d > 0 ? 'up' : d < 0 ? 'down' : ''}`}>{sign(d)}{format(Math.abs(d))} since {x[base]}</span>}
            </button>
          );
        })}
      </div>
      <div className="ch-plot">
        <svg className="chart chart-wide" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Line chart" tabIndex={0}
          onMouseLeave={() => setHover(null)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') { e.preventDefault(); setHover((v) => Math.min(last, v === null ? 0 : v + 1)); }
            if (e.key === 'ArrowLeft') { e.preventDefault(); setHover((v) => Math.max(0, v === null ? last : v - 1)); }
            if (e.key === 'Escape') setHover(null);
          }}
          onMouseMove={(e) => {
            const box = e.currentTarget.getBoundingClientRect();
            const rel = ((e.clientX - box.left) / box.width) * w;
            const i = x.length <= 1 ? 0 : Math.round(((rel - pad.l) / plotW) * last);
            setHover(Math.min(last, Math.max(0, i)));
          }}>
          <defs>
            {shown.map((s) => (
              <linearGradient key={s.id} id={s.id} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity={0.12} />
                <stop offset="100%" stopColor={s.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          {[0, 0.25, 0.5, 0.75, 1].map((p) => (
            <g key={p}>
              <line x1={pad.l} x2={w - pad.r} y1={py(p * max)} y2={py(p * max)} className={p === 0 ? 'ch-axis' : 'ch-grid'} />
              <text x={pad.l - 8} y={py(p * max) + 4} textAnchor="end" className="ch-tick">{compact(p * max)}</text>
            </g>
          ))}
          {xTicks.map((i) => (
            <text key={i} x={px(i)} y={h - 10} textAnchor={i === 0 ? 'start' : i === last ? 'end' : 'middle'} className="ch-tick">{x[i]}</text>
          ))}
          {shown.map((s, si) => {
            const pts = s.values.map((v, i) => [px(i), py(v)] as const);
            const d = smoothPath(pts);
            // Only the largest line keeps a faint area underneath; the others stay clean lines.
            const isTop = si === topIndex;
            return (
              <g key={s.name} className="ch-series">
                {x.length > 1 && isTop && <path d={`${d} L${px(last)},${py(0)} L${px(0)},${py(0)} Z`} fill={`url(#${s.id})`} />}
                <path className="ch-line" pathLength={1} d={d} fill="none" stroke={s.color} strokeWidth={2.75} strokeLinejoin="round" strokeLinecap="round" />
                {hover !== null && <circle cx={px(hover)} cy={py(s.values[hover] ?? 0)} r={5} fill={s.color} stroke="var(--surface)" strokeWidth={2} />}
                <circle cx={px(last)} cy={py(s.values[last] ?? 0)} r={4.5} fill="var(--surface)" stroke={s.color} strokeWidth={2.5} />
              </g>
            );
          })}
          {/* End labels: the line's name and latest value, pushed apart so they never overlap. */}
          {pad.r > 20 && endLabels.map((l) => (
            <g key={l.name}>
              {Math.abs(l.y - l.at) > 2 && <line x1={px(last) + 6} x2={px(last) + 12} y1={l.at} y2={l.y - 4} stroke={l.color} strokeWidth={1} />}
              <text x={px(last) + 14} y={l.y} className="ch-end"><tspan fill={l.color} className="ch-end-name">{l.name}</tspan><tspan x={px(last) + 14} dy={13}>{compact(l.value)}</tspan></text>
            </g>
          ))}
          {hover !== null && <line x1={px(hover)} x2={px(hover)} y1={pad.t} y2={h - pad.b} className="ch-cross" />}
        </svg>
        {hover !== null && (
          <div className={`ch-float${tipLeft > 60 ? ' left' : ''}`} style={{ left: `${tipLeft}%` }} role="status">
            <b>{x[hover]}</b>
            {[...shown].sort((a, b) => (b.values[hover] ?? 0) - (a.values[hover] ?? 0)).map((s) => {
              const d = change(s, hover);
              return (
                <span key={s.name} className="ch-float-row">
                  <i style={{ background: s.color }} />{s.name}<b>{format(s.values[hover] ?? 0)}</b>
                  {hover > 0 && <em className={d > 0 ? 'up' : d < 0 ? 'down' : ''}>{sign(d)}{compact(Math.abs(d))}</em>}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </figure>
  );
}

/** A round axis maximum just above the data (1, 2, 2.5 or 5 × a power of ten). */
function niceMax(v: number): number {
  const p = 10 ** Math.floor(Math.log10(v));
  return ([1, 2, 2.5, 5, 10].map((m) => m * p).find((m) => m >= v * 1.05) ?? 10 * p);
}

/** Part-to-whole for a handful of categories, always with a legend. */
export function DonutChart({ data, format }: { data: Datum[]; format: (n: number) => string }) {
  const items = foldOther(data.filter((d) => d.value > 0));
  const total = items.reduce((t, d) => t + d.value, 0);
  if (!total) return <p className="empty-line">No data.</p>;
  const r = 70;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="donut">
      <svg viewBox="0 0 180 180" className="chart donut-svg" role="img" aria-label="Donut chart">
        <g transform="rotate(-90 90 90)">
          {items.map((d, i) => {
            const len = (d.value / total) * c;
            // A 2px surface gap separates neighbouring segments.
            const seg = <circle key={d.label} cx={90} cy={90} r={r} fill="none" stroke={SERIES[i]} strokeWidth={22}
              strokeDasharray={`${Math.max(0, len - 2)} ${c}`} strokeDashoffset={-offset}><title>{`${d.label}: ${format(d.value)}`}</title></circle>;
            offset += len;
            return seg;
          })}
        </g>
        <text x={90} y={96} textAnchor="middle" className="ch-center">{compact(total)}</text>
      </svg>
      <ul className="legend vertical">
        {items.map((d, i) => <li key={d.label}><i style={{ background: SERIES[i] }} />{d.label} <span className="muted">{Math.round((d.value / total) * 100)}%</span></li>)}
      </ul>
    </div>
  );
}

export function Metric({ value, label, format }: { value: number | null; label: string; format: (n: number) => string }) {
  return (
    <div className="metric">
      <b>{value === null ? '—' : format(value)}</b>
      <span className="muted">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// From report results to chart data
// ---------------------------------------------------------------------------

/** Display text for a group key, using the registry for picklists and the server's labels for ids. */
export function keyLabel(obj: ObjectDef, field: string, key: string | null, labels: Record<string, string>): string {
  if (key === null || key === '') return '(blank)';
  const f = getField(obj, field);
  if (f?.type === 'picklist') return f.options?.find((o) => o.value === key)?.label ?? key;
  if (f?.type === 'boolean') return key === 'true' ? 'Yes' : 'No';
  return labels[key] ?? key;
}

/** One value per first-level group, for the chosen summary. */
export function firstLevel(r: ReportResult, aggregateIndex: number, obj: ObjectDef): Datum[] {
  const g0 = r.definition.rowGroupings[0];
  if (!g0) return [];
  return r.groups
    .filter((g) => !g.rolledUp[0] && g.rolledUp.slice(1).every(Boolean))
    .map((g) => ({ label: keyLabel(obj, g0.field, g.keys[0] ?? null, r.labels), value: g.values[aggregateIndex] ?? 0 }));
}

/** Formats a summary value: currency for currency fields, plain numbers otherwise. */
export function valueFormatter(r: ReportResult, aggregateIndex: number, currency: string, obj: ObjectDef): (n: number) => string {
  const a = r.definition.aggregates[aggregateIndex];
  const f = a?.field ? getField(obj, a.field) : undefined;
  if (f?.type === 'currency' && a?.fn !== 'count') return (n) => formatMoney(n, currency);
  if (f?.type === 'percent') return (n) => `${n.toFixed(1)}%`;
  return (n) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n);
}

/** Renders a report result as the chosen chart, falling back to a table where a chart doesn't fit. */
export function ReportChart({ result, chart, aggregateIndex = 0, currency }: { result: ReportResult; chart: ChartType; aggregateIndex?: number; currency: string }) {
  const { schema } = useSession();
  const obj = schema[result.definition.object];
  const fmt = valueFormatter(result, aggregateIndex, currency, obj);
  const label = result.aggregateLabels[aggregateIndex] ?? '';
  // Keyed by chart type: switching bars / lines / donut fades the old one out and the new one draws itself in.
  const morph = (node: ReactNode) => <div key={chart} className="chart-morph">{node}</div>;
  if (chart === 'metric' || !result.definition.rowGroupings.length) {
    return morph(<Metric value={grandTotal(result)?.values[aggregateIndex] ?? null} label={label} format={fmt} />);
  }
  const data = firstLevel(result, aggregateIndex, obj);
  if (chart === 'bar') return morph(<BarChart data={data} format={fmt} />);
  if (chart === 'column') return morph(<ColumnChart data={data} format={fmt} />);
  if (chart === 'donut') return morph(<DonutChart data={data} format={fmt} />);
  if (chart === 'line') return morph(<LineChart x={data.map((d) => d.label)} series={[{ name: label, values: data.map((d) => d.value) }]} format={fmt} />);
  return morph(
    <table className="mini">
      <thead><tr><th>{getField(obj, result.definition.rowGroupings[0]!.field)?.label}</th><th className="r">{label}</th></tr></thead>
      <tbody>{data.map((d) => <tr key={d.label}><td>{d.label}</td><td className="num">{fmt(d.value)}</td></tr>)}</tbody>
    </table>,
  );
}
